import { useRef, useState } from 'react';

import { Box, TextInput, ValidatedTextInput } from '@opendoor/bricks-next';
import type { useQuery } from 'platform/apollo';
import type { TextInputSize } from 'platform/components/AccountPreferences';
import { genericErrorText } from 'platform/components/AccountPreferences';
import type { CustomerDetailsQuery_customerService_OdProtosCustomerV1Services_Customer_Query_getCurrentCustomer_OdProtosCustomerV1Services_CustomerResponses_GetCurrentCustomer_customer_OdProtosCustomerV1Data_Customer as Customer } from 'platform/graphql/__generated__/useCustomerDetails';
import { useUpdatePrimaryPhoneNumber } from 'platform/graphql/useCustomerDetails';
import type { ShowToastFn } from 'shared/hooks/useToast';

import { SectionHeader } from './SectionHeader';

const getFormattedPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) return '';

  return phoneNumber.indexOf('+1') === 0 ? phoneNumber.slice(2) : phoneNumber;
};

/**
 * Displays phone number and email and allows the user to edit it.
 *
 */
export const ContactInfoContainer = ({
  customerInfo,
  inputSize,
  refetch,
  showToast,
}: {
  customerInfo?: Customer | null;
  inputSize: TextInputSize;
  refetch: ReturnType<typeof useQuery>['refetch'];
  showToast: ShowToastFn;
}) => {
  const formatedPhoneNumber = getFormattedPhoneNumber(
    customerInfo?.primaryPhoneNumber?.number?.value,
  );

  const savedFormState = useRef<{
    phoneNumber?: string;
  }>({
    phoneNumber: formatedPhoneNumber,
  });

  const { updatePrimaryPhoneNumber, loading: updatingCustomerPhoneNumber } =
    useUpdatePrimaryPhoneNumber();

  const [phoneNumber, setPhoneNumber] = useState<string>(formatedPhoneNumber);
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<boolean>(false);

  const [email] = useState<string>(customerInfo?.primaryEmail?.address?.value || '');

  const isFormValid = () => {
    if (phoneNumber.length > 0 && !isPhoneNumberValid) {
      return false;
    }

    return true;
  };

  const updatePhoneNumber = async () => {
    try {
      const { data, error } = await updatePrimaryPhoneNumber({
        variables: {
          newPhoneNumber: phoneNumber,
        },
      });

      if (error) {
        setPhoneNumberError(genericErrorText);
        return;
      }

      // Update current form state for dirty check and passing old phone number
      const updatedCustomer = data?.customerService?.setPrimaryPhoneNumber?.customer;

      const formState = {
        phoneNumber: updatedCustomer?.primaryPhoneNumber?.number?.value || '',
      };

      //Forcing refetching if a new phone number is added for first time to enable comms preferences for SMS
      if (
        savedFormState.current.phoneNumber === '' &&
        updatedCustomer?.primaryPhoneNumber?.number?.value
      ) {
        void refetch();
      }

      savedFormState.current = formState;
      showToast({
        type: 'success',
        message: 'Your contact info has been updated',
        analyticsName: 'cake-contact-info-success-toast',
      });
    } catch {
      setPhoneNumberError(genericErrorText);
    }
  };

  const updateContactDetails = () => {
    if (!isFormValid()) return;

    const oldPhoneNumber = savedFormState.current.phoneNumber?.replace(/[+ ]/g, ''),
      newPhoneNumber = phoneNumber?.replace(/[+ ]/g, '');

    if (oldPhoneNumber !== newPhoneNumber && phoneNumber.length > 0) {
      void updatePhoneNumber();
    }
  };

  return (
    <Box
      testID="contact-info-container"
      gap="$12x"
      $largerThanSM={{
        gap: '$16x',
      }}
    >
      <SectionHeader
        header="Contact info"
        saveButtonId="save-contact-info"
        analyticsName="save-contact-info"
        onSave={() => void updateContactDetails()}
        inputSize={inputSize}
        updating={updatingCustomerPhoneNumber}
      />
      <Box
        $largerThanSM={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '$44x',
        }}
        gap="$12x"
      >
        <Box flex={1}>
          <ValidatedTextInput
            id="phone-number-field"
            title="Phone"
            size={inputSize}
            prefilledValue={formatedPhoneNumber}
            format="telephoneNumber"
            onValidationChange={(validationState) => {
              setIsPhoneNumberValid(validationState === 'valid');
            }}
            errorText={phoneNumberError}
            getValue={(value) => {
              setPhoneNumber(value.unformattedValue || '');
              setPhoneNumberError('');
            }}
          />
        </Box>

        <Box flex={1}>
          <TextInput id="email-field" size={inputSize} title="Email" disabled value={email} />
        </Box>
      </Box>
    </Box>
  );
};
